import { useCallback } from 'react'
import { useAuthHttp } from '../../hooks'
import { toAlpha2 } from './countries'
import type { ZoneLocation } from './types'

export type { ZoneLocation } from './types'

export interface GeoPoint {
  lat: number
  lon: number
}

export interface GeocodeResult {
  address: string
  geoPoint: GeoPoint
}

export interface AddressOption {
  id: string
  // Human-readable label, e.g. "IP, Ipswich". The dropdown shows this plus lat/lng below it.
  title: string
  geoPoint: GeoPoint
}

export interface GeocodeError {
  message: string
  code: 'NOT_FOUND' | 'NETWORK_ERROR' | 'API_ERROR'
}

export type GeocodeWithOptionsResponse =
  | { success: true; data: GeocodeResult; options: AddressOption[] }
  | { success: false; error: GeocodeError }

export interface LocationEntry extends ZoneLocation {
  isLoading?: boolean
  error?: string
  addressOptions?: AddressOption[]
}

interface PostcodeResolveCandidate {
  matchedPrefix: string
  placeName: string | null
  lat: number
  lon: number
}

interface PostcodeResolveResult {
  input: string
  status: 'exact' | 'prefix' | 'not_found'
  candidates?: PostcodeResolveCandidate[]
  error?: string
}

interface PostcodeResolveResponse {
  results?: PostcodeResolveResult[]
}

const RESOLVE_ENDPOINT = '/Postcodes/Resolve'
const MAX_CANDIDATES = 5

export function extractCity(address: string): string {
  const parts = address.split(',').map((p) => p.trim())
  return parts[1] || parts[0] || address
}

export function applyGeocodeResult(entry: LocationEntry, result: GeocodeWithOptionsResponse): LocationEntry {
  if (result.success) {
    const firstOption = result.options[0]
    if (firstOption) {
      return {
        ...entry,
        city: extractCity(firstOption.title),
        geoPoint: firstOption.geoPoint,
        isLoading: false,
        error: undefined,
        addressOptions: result.options,
        status: 'resolved',
        resolveError: undefined,
      }
    }
  }
  // 'not_found' = backend returned a result but with no candidates (postcode genuinely missing
  // from the cache). 'error' = network/API failure. Save-side validation treats both as blockers.
  const isNotFound = !result.success && result.error?.code === 'NOT_FOUND'
  const message = result.success ? 'No results found' : result.error?.message || 'Lookup failed'
  return {
    ...entry,
    isLoading: false,
    error: message,
    addressOptions: undefined,
    status: isNotFound ? 'not_found' : 'error',
    resolveError: message,
  }
}

function describeCandidate(candidate: PostcodeResolveCandidate): AddressOption {
  // Display the matched prefix + place name only. We deliberately do NOT surface the bucket's
  // doc count — the candidate represents a single resolved centre point (the geo_centroid of the
  // matching documents), and labelling it "(N postcodes)" implies a summary the user didn't ask
  // for. The lat/lng shown in the dropdown is the source of truth.
  const place = candidate.placeName?.trim() || candidate.matchedPrefix
  const titleParts = [candidate.matchedPrefix]
  if (candidate.placeName && candidate.placeName !== candidate.matchedPrefix) {
    titleParts.push(candidate.placeName)
  }
  const label = titleParts.join(', ')
  return {
    id: `${candidate.matchedPrefix}::${place}`,
    title: label,
    geoPoint: { lat: candidate.lat, lon: candidate.lon },
  }
}

function mapResolveResult(result: PostcodeResolveResult): GeocodeWithOptionsResponse {
  const candidates = result.candidates ?? []
  if (result.status === 'not_found' || candidates.length === 0) {
    return {
      success: false,
      error: { message: result.error || 'No match in postcode cache', code: 'NOT_FOUND' },
    }
  }
  const options: AddressOption[] = candidates.map(describeCandidate)
  const first = options[0]!
  return {
    success: true,
    data: { address: first.title, geoPoint: first.geoPoint },
    options,
  }
}

/**
 * Hook returning batch + single postcode resolution functions. Wraps `useAuthHttp` so requests
 * carry the bearer token and benefit from automatic refresh on expiry.
 */
export function useResolvePostcodes() {
  const authHttp = useAuthHttp()

  const resolveBatch = useCallback(
    async (inputs: string[], countryCode: string): Promise<GeocodeWithOptionsResponse[]> => {
      if (inputs.length === 0) return []

      // Rate cards use ISO 3166-1 alpha-3 ("GBR"); the postcodes cache uses alpha-2 ("GB"). Convert.
      // Bad country codes are an upstream contract violation, not a transient resolution failure —
      // surface them as a thrown Error so the caller can show a single zone-level message instead
      // of N identical per-row errors. Callers wrap this in try/catch (see NetworkRatecardEditor's
      // auto-resolve effect and LocationEditorPanel's handleAddPostcodes).
      const country = toAlpha2(countryCode)
      if (country.length !== 2) {
        throw new Error(
          `Cannot resolve postcodes — unsupported country code "${countryCode}". Expected an ISO 3166-1 code (e.g. "GBR" or "GB").`
        )
      }

      // Pass user input verbatim. The backend `postcodes` index has a normaliser
      // (whitespace_strip + uppercase) that handles case/whitespace variations at index AND query
      // time, so any form the user types ("KT19 0RL", "kt190rl", "KT 19 0rl") matches the same docs.
      try {
        const response = await authHttp.post<PostcodeResolveResponse>(RESOLVE_ENDPOINT, {
          postcodes: inputs,
          countryCode: country,
          maxCandidatesPerInput: MAX_CANDIDATES,
        })
        const data = response.data
        const byInput = new Map<string, PostcodeResolveResult>()
        for (const r of data?.results ?? []) {
          if (r?.input != null) byInput.set(r.input, r)
        }
        return inputs.map((input) => {
          const result = byInput.get(input)
          if (!result) {
            return {
              success: false,
              error: { message: 'No response for postcode', code: 'NOT_FOUND' } as GeocodeError,
            }
          }
          return mapResolveResult(result)
        })
      } catch (err: any) {
        const status = err?.response?.status
        const message =
          status != null
            ? `Postcode resolve failed (${status})`
            : err instanceof Error
            ? err.message
            : 'Network error - please try again'
        const code: GeocodeError['code'] = status != null ? 'API_ERROR' : 'NETWORK_ERROR'
        const error: GeocodeError = { message, code }
        return inputs.map(() => ({ success: false, error }))
      }
    },
    [authHttp]
  )

  const resolveSingle = useCallback(
    async (query: string, countryCode: string): Promise<GeocodeWithOptionsResponse> => {
      if (!query || query.trim().length === 0) {
        return { success: false, error: { message: 'Please enter a location', code: 'NOT_FOUND' } }
      }
      if (!countryCode) {
        return { success: false, error: { message: 'Country code required', code: 'API_ERROR' } }
      }
      const results = await resolveBatch([query], countryCode)
      return (
        results[0] ?? {
          success: false,
          error: { message: 'No response for postcode', code: 'NOT_FOUND' },
        }
      )
    },
    [resolveBatch]
  )

  return { resolveBatch, resolveSingle }
}
